import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useLocation, useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api';
import ImageViewer from '../components/ImageViewer';
import { ThemeContext } from '../ThemeContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Clock, Target, Calendar, CheckCircle, XCircle, AlertCircle } from 'lucide-react';

// Glassmorphism Card with Animations
const Card = ({ children, className = '', delay = 0 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isDarkMode } = useContext(ThemeContext);
  
  const hoverColor = isDarkMode ? '#ff9e42' : '#06bae4';
  
  // Convert hex to rgba for shadows
  const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.6,
        delay: delay,
        type: "spring",
        stiffness: 100,
      }}
      whileHover={{
        scale: 1.02,
        y: -5,
        transition: { duration: 0.2 },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className={`relative backdrop-blur-xl bg-white/80 dark:bg-gray-800/80 rounded-xl sm:rounded-2xl md:rounded-3xl shadow-2xl p-3 sm:p-4 md:p-6 border transition-all duration-300 ${className}`}
      style={{
        boxShadow: isHovered
          ? `0 20px 60px ${hexToRgba(hoverColor, 0.25)}, 0 0 40px ${hexToRgba(hoverColor, 0.2)}`
          : '0 10px 40px rgba(0, 0, 0, 0.1)',
        borderColor: isHovered 
          ? hexToRgba(hoverColor, 0.4)
          : isDarkMode ? 'rgba(55, 65, 81, 0.5)' : 'rgba(255, 255, 255, 0.2)',
      }}
    >
      {children}
    </motion.div>
  );
};

// Module-level animation state tracking
let hasAnimatedInSession = false;

const AnswerDetailPage = () => {
    const [question, setQuestion] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const hasAnimatedRef = useRef(false);
    const { isDarkMode } = useContext(ThemeContext);

    const location = useLocation();
    const navigate = useNavigate();
    const { scoreId } = useParams();
    // answerDetails contains the user's specific attempt (Score model instance)
    const { answerDetails } = location.state || {};

    // Title gradient memoization - must be before early returns
    const titleGradient = useMemo(() => {
        return `linear-gradient(90deg, ${isDarkMode ? '#ff7a00' : '#212D82'} 0%, ${isDarkMode ? '#ff7a00' : '#212D82'} 40%, rgba(255, 122, 0, 0.8) 50%, ${isDarkMode ? '#ff7a00' : '#212D82'} 60%, ${isDarkMode ? '#ff7a00' : '#212D82'} 100%)`;
    }, [isDarkMode]);

    // Set animation state on mount - must be before early returns
    useEffect(() => {
        if (!hasAnimatedInSession) {
            hasAnimatedInSession = true;
            hasAnimatedRef.current = false;
        } else {
            hasAnimatedRef.current = true;
        }
    }, []);

    useEffect(() => {
        if (!answerDetails) {
            // If the user lands here directly without state, we can't show the details.
            // A more robust solution might fetch the score details if an API existed.
            navigate('/answer-history');
            return;
        }

        setIsLoading(true);
        api.get(`/api/solo/get_question/${answerDetails.question_id}/`)
            .then(response => {
                setQuestion(response.data);
                setIsLoading(false);
            })
            .catch(err => {
                console.error("Error fetching question details:", err);
                setError('Failed to load question details.');
                setIsLoading(false);
            });

    }, [answerDetails, navigate]);

    if (isLoading) {
        return (
            <div className="min-h-screen flex flex-col justify-center items-center text-center bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-24 pl-0 md:pl-24">
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                        className="inline-block mb-4"
                    >
                        <Clock className="w-16 h-16 text-cyan-500" />
                    </motion.div>
                    <h1 className="text-2xl font-bold text-gray-600 dark:text-gray-300 font-zonaProRegular">Loading question details...</h1>
                </motion.div>
            </div>
        );
    }

    if (error) {
         return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pb-24 pl-0 md:pl-24">
                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="backdrop-blur-xl bg-white/90 dark:bg-gray-800/90 rounded-3xl p-8 text-center shadow-2xl border border-white/20 dark:border-gray-700/50 max-w-md"
                >
                    <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                    <p className="text-xl text-red-500 dark:text-red-400 mb-4 font-zonaProRegular">{error}</p>
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                        <Link 
                            to="/answer-history" 
                            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-full hover:shadow-lg transition-all font-zonaProRegular"
                        >
                            <ArrowLeft size={18} />
                            Back to Answer History
                        </Link>
                    </motion.div>
                </motion.div>
            </div>
        );
    }

    const answerOptionKeys = ['A', 'B', 'C', 'D'];
    const currentAnswerOptions = question ? answerOptionKeys.map(key => {
        const value = question['option_' + key.toLowerCase()];
        return value ? { key, value } : null;
    }).filter(opt => opt !== null) : [];

    const getResultHeader = () => {
        switch (answerDetails.question_status) {
            case 'correct': return "CORRECT!";
            case 'incorrect': return "INCORRECT";
            case 'unanswered': return "UNANSWERED";
            default: return "Result";
        }
    }
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
    const getResultBorderColor = () => {
         switch (answerDetails.question_status) {
            case 'correct': return 'border-green-500 bg-green-50 dark:bg-green-900/20 dark:border-green-400';
            case 'incorrect': return 'border-red-500 bg-red-50 dark:bg-red-900/20 dark:border-red-400';
            case 'unanswered': return 'border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20 dark:border-yellow-400';
            default: return 'border-gray-300 bg-gray-50 dark:bg-gray-800 dark:border-gray-600';
        }
    }
    
    const getResultTextColor = () => {
        switch (answerDetails.question_status) {
            case 'correct': return 'text-green-700 dark:text-green-300';
            case 'incorrect': return 'text-red-700 dark:text-red-300';
            case 'unanswered': return 'text-yellow-700 dark:text-yellow-300';
            default: return 'text-gray-700 dark:text-gray-300';
        }
    }

    const getStatusIcon = () => {
        switch (answerDetails?.question_status) {
            case 'correct': return <CheckCircle className="w-6 h-6 text-green-500 dark:text-green-400" />;
            case 'incorrect': return <XCircle className="w-6 h-6 text-red-500 dark:text-red-400" />;
            case 'unanswered': return <AlertCircle className="w-6 h-6 text-yellow-500 dark:text-yellow-400" />;
            default: return null;
        }
    }

    // Function to render text with π symbols in font-sans
    const renderTextWithPi = (text) => {
        if (!text || typeof text !== 'string') return text || '';
        if (!text.includes('π')) return text;
        
        return text.split('π').map((part, index) => (
            <React.Fragment key={index}>
                {part}
                {index < text.split('π').length - 1 && (
                    <span className="font-sans">π</span>
                )}
            </React.Fragment>
        ));
    };

  return (
    <div className="min-h-screen flex flex-col relative pb-24 pl-0 md:pl-24 overflow-hidden bg-gradient-to-br from-sky-50 to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 font-zonaProRegular">
      {/* Main Content */}
      <div className="relative z-20 px-4 sm:px-6 lg:px-8 py-6 w-full max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={hasAnimatedRef.current ? false : { opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, type: "spring" }}
          className="text-center mb-8"
        >
          <h1
            key={`title-${isDarkMode}`}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold font-zonaProUltra mb-4 relative inline-block"
            style={{
              color: isDarkMode ? '#ff7a00' : '#212D82',
              background: titleGradient,
              backgroundSize: '200% 100%',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              animation: 'shine 10s linear infinite',
              lineHeight: '1.2',
              paddingBottom: '0.25rem'
            }}
          >
            Question Details
          </h1>
          <motion.p
            className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 font-zonaProRegular"
            initial={hasAnimatedRef.current ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            Review your question attempt and results
          </motion.p>
        </motion.div>

        {/* Summary Card: Difficulty + Time + Date */}
        {(question?.difficulty_level ||
          answerDetails?.time_spent !== undefined ||
          answerDetails?.created_date) && (
          <Card className="text-left mb-6" delay={0.2}>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="flex items-center gap-3">
                <Target className="w-5 h-5 text-cyan-500 dark:text-cyan-400 flex-shrink-0" />
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 font-zonaProRegular mb-1">Difficulty</p>
                  <p
                    className={`text-base sm:text-lg font-zonaProBold ${
                      question?.difficulty_level === 'Hard'
                        ? 'text-red-600 dark:text-red-400'
                        : question?.difficulty_level === 'Medium'
                        ? 'text-yellow-600 dark:text-yellow-400'
                        : 'text-green-600 dark:text-green-400'
                    }`}
                  >
                    {question?.difficulty_level || 'N/A'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Clock className="w-5 h-5 text-cyan-500 dark:text-cyan-400 flex-shrink-0" />
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 font-zonaProRegular mb-1">Time Spent</p>
                  <p className="text-base sm:text-lg font-zonaProBold text-gray-800 dark:text-gray-200">
                    {answerDetails?.time_spent ? `${answerDetails.time_spent}s` : 'N/A'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Calendar className="w-5 h-5 text-cyan-500 dark:text-cyan-400 flex-shrink-0" />
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 font-zonaProRegular mb-1">Date</p>
                  <p className="text-sm sm:text-base font-zonaProBold text-gray-800 dark:text-gray-200 truncate">
                    {answerDetails?.created_date ? formatDate(answerDetails.created_date) : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 w-full">
          {/* Left Side: Question & Feedback */}
          <div className="space-y-5">
            <Card delay={0.3}>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-200 font-zonaProUltra mb-4">
                Question
              </h2>
              {question?.image_name && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 }}
                  className="my-4 rounded-xl overflow-hidden shadow-lg max-h-80 flex justify-center items-center bg-gray-100 dark:bg-gray-800"
                >
                  <ImageViewer
                    fileName={question.image_name}
                    alt="Question image"
                    className="max-h-full max-w-full object-contain"
                  />
                </motion.div>
              )}
              <p className="text-base sm:text-lg md:text-xl text-gray-700 dark:text-gray-300 text-left leading-relaxed whitespace-pre-line">
                {renderTextWithPi(question?.question_choice || question?.content || '')}
              </p>
            </Card>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className={`mt-6 p-5 rounded-xl border-l-4 ${getResultBorderColor()}`}
            >
              <div className="flex items-center gap-3 mb-3">
                {getStatusIcon()}
                <h3 className={`text-xl font-semibold font-zonaProUltra ${getResultTextColor()}`}>
                  {getResultHeader()}
                </h3>
              </div>
              {question?.feedback && (
                <p className={`mb-3 ${getResultTextColor()}`}>
                  {renderTextWithPi(question.feedback)}
                </p>
              )}
              {answerDetails?.question_status !== 'unanswered' && answerDetails?.points !== undefined && (
                <div className={`text-lg font-semibold ${getResultTextColor()}`}>
                  Points:{' '}
                  <span className="font-bold">
                    {answerDetails.points > 0
                      ? `+${answerDetails.points}`
                      : answerDetails.points}
                  </span>
                </div>
              )}
            </motion.div>
          </div>

          {/* Right Side: Answer Options */}
          <div className="mt-8 md:mt-0">
            <Card delay={0.4}>
              <h2 className="text-lg sm:text-xl md:text-2xl font-zonaProUltra text-gray-800 dark:text-gray-200 mb-4">
                Results
              </h2>
              {question?.question_type === 'Grid-in' ? (
                <div className="space-y-3">
                  {answerDetails?.question_status === 'correct' && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="w-full text-left p-4 rounded-xl border-2 font-medium bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-400 text-green-700 dark:text-green-300"
                    >
                      <span className="font-bold mr-2">Your Answer:</span>
                      {renderTextWithPi(answerDetails?.option_selected || '')}
                    </motion.div>
                  )}

                  {answerDetails?.question_status === 'incorrect' && (
                    <>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="w-full text-left p-4 rounded-xl border-2 font-medium bg-red-50 dark:bg-red-900/20 border-red-500 dark:border-red-400 text-red-700 dark:text-red-300"
                      >
                        <span className="font-bold mr-2">Your Answer:</span>
                        {renderTextWithPi(answerDetails?.option_selected || '')}
                      </motion.div>
                      <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.1 }}
                        className="w-full text-left p-4 rounded-xl border-2 font-medium bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-400 text-green-700 dark:text-green-300"
                      >
                        <span className="font-bold mr-2">Correct Answer:</span>
                        {renderTextWithPi(question?.answer || '')}
                      </motion.div>
                    </>
                  )}

                  {answerDetails?.question_status === 'unanswered' && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="w-full text-left p-4 rounded-xl border-2 font-medium bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-400 text-green-700 dark:text-green-300"
                    >
                      <span className="font-bold mr-2">Correct Answer:</span>
                      {renderTextWithPi(question?.answer || '')}
                    </motion.div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  {currentAnswerOptions.map((opt, idx) => {
                    const isUserAnswer = answerDetails?.option_selected === opt.key;
                    const isCorrectAnswer = question?.answer === opt.value;

                    let classes =
                      'w-full text-left p-4 rounded-xl border-2 font-medium transition-all';

                    if (isUserAnswer && !isCorrectAnswer) {
                      classes += ' bg-red-50 dark:bg-red-900/20 border-red-400 dark:border-red-500 text-red-700 dark:text-red-300';
                    } else if (isCorrectAnswer) {
                      classes += ' bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-400 text-green-700 dark:text-green-300';
                    } else {
                      classes +=
                        ' bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400 opacity-75';
                    }

                    return (
                      <motion.div
                        key={opt.key}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 + idx * 0.1 }}
                        whileHover={{ scale: 1.02, x: 4 }}
                        className={classes}
                      >
                        <span className="font-bold mr-2">{opt.key})</span>
                        {renderTextWithPi(opt.value)}
                      </motion.div>
                    );
                  })}
                </div>
              )}
            </Card>
          </div>
        </div>

        {/* Back link at bottom */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-10 text-center"
        >
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link
              to="/answer-history"
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-cyan-500 to-teal-500 dark:from-cyan-600 dark:to-teal-600 text-white rounded-full hover:shadow-lg transition-all font-zonaProRegular"
            >
              <ArrowLeft size={18} />
              Back to Answer History
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default AnswerDetailPage; 